import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d63dtaj0723ekv2itbp0';

const serviceOptions = [
  'Cloud Solutions',
  'Cybersecurity',
  'AI & Analytics',
  'Custom Development',
  'IT Support',
  'Digital Strategy',
  'Other'
];

const timeSlots = [
  '9:00 AM',
  '10:00 AM',
  '11:00 AM',
  '12:00 PM',
  '1:00 PM',
  '2:00 PM',
  '3:00 PM',
  '4:00 PM'
];

interface BookingDetails {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  service: string;
  preferredDate: string;
  preferredTime: string;
  message: string;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

function ConfirmationReceipt({ details, onBookAnother }: { details: BookingDetails; onBookAnother: () => void }) {
  const confirmationId = `PDT-${Date.now().toString(36).toUpperCase().slice(-6)}`;

  return (
    <div className="py-4">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
          <i className="ri-check-double-line text-3xl text-green-600"></i>
        </div>
        <h3 className="text-2xl font-bold text-[#1A2332] mb-2">Appointment Confirmed!</h3>
        <p className="text-sm text-gray-500 max-w-sm mx-auto">
          A confirmation email has been sent to <strong className="text-[#1A2332]">{details.email}</strong>. Please check your inbox.
        </p>
      </div>

      {/* Confirmation Card */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-6">
        {/* Card Header */}
        <div className="bg-gradient-to-r from-[#0066CC] to-[#0055AA] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
              <i className="ri-calendar-check-line text-lg text-white"></i>
            </div>
            <div>
              <p className="text-white/70 text-xs font-medium">Confirmation ID</p>
              <p className="text-white font-bold text-sm tracking-wide">{confirmationId}</p>
            </div>
          </div>
          <span className="px-3 py-1 bg-green-400/20 text-green-100 text-xs font-semibold rounded-full whitespace-nowrap">
            Pending Confirmation
          </span>
        </div>

        {/* Appointment Details */}
        <div className="p-6 space-y-5">
          {/* Date & Time highlight */}
          <div className="flex items-center gap-4 bg-[#F0F7FF] rounded-xl p-4">
            <div className="w-12 h-12 rounded-xl bg-[#0066CC]/10 flex items-center justify-center shrink-0">
              <i className="ri-time-line text-xl text-[#0066CC]"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-[#1A2332]">{formatDate(details.preferredDate)}</p>
              <p className="text-sm text-[#0066CC] font-medium">{details.preferredTime} &middot; 30 Minutes</p>
            </div>
          </div>

          {/* Detail rows */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">Name</p>
              <p className="text-sm font-semibold text-[#1A2332]">{details.fullName}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">Email</p>
              <p className="text-sm font-semibold text-[#1A2332]">{details.email}</p>
            </div>
            {details.phone && (
              <div>
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">Phone</p>
                <p className="text-sm font-semibold text-[#1A2332]">{details.phone}</p>
              </div>
            )}
            {details.company && (
              <div>
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">Company</p>
                <p className="text-sm font-semibold text-[#1A2332]">{details.company}</p>
              </div>
            )}
            <div className="sm:col-span-2">
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">Service</p>
              <p className="text-sm font-semibold text-[#1A2332]">{details.service}</p>
            </div>
          </div>

          {details.message && (
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">Additional Details</p>
              <p className="text-sm text-gray-600 leading-relaxed">{details.message}</p>
            </div>
          )}
        </div>
      </div>

      {/* What\u2019s Next */}
      <div className="bg-[#FFFBEB] rounded-xl p-5 mb-6 border border-amber-100">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-information-line text-lg text-amber-600"></i>
          </div>
          <div>
            <p className="text-sm font-semibold text-amber-800 mb-1">What happens next?</p>
            <ul className="text-sm text-amber-700 space-y-1">
              <li className="flex items-start gap-2">
                <span className="text-amber-400 mt-0.5">1.</span>
                Our team will review your request within 24 hours
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-400 mt-0.5">2.</span>
                You\u2019ll receive a calendar invite at <strong>{details.email}</strong>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-400 mt-0.5">3.</span>
                A senior solutions architect will join your session
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={onBookAnother}
          className="flex-1 py-3.5 bg-[#0066CC] text-white rounded-lg font-semibold text-sm hover:bg-[#0055AA] transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
        >
          <i className="ri-add-line text-lg"></i>
          Book Another Appointment
        </button>
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="flex-1 py-3.5 bg-white border border-gray-200 text-[#1A2332] rounded-lg font-semibold text-sm hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
        >
          <i className="ri-arrow-up-line text-lg"></i>
          Back to Top
        </button>
      </div>
    </div>
  );
}

export default function BookingForm() {
  const [formData, setFormData] = useState<BookingDetails>({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    preferredDate: '',
    preferredTime: '',
    message: ''
  });
  const [confirmedDetails, setConfirmedDetails] = useState<BookingDetails | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const body = new URLSearchParams();
    body.append('fullName', formData.fullName);
    body.append('email', formData.email);
    body.append('phone', formData.phone);
    body.append('company', formData.company);
    body.append('service', formData.service);
    body.append('preferredDate', formData.preferredDate);
    body.append('preferredTime', formData.preferredTime);
    body.append('message', formData.message);

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      });
      if (res.ok) {
        setConfirmedDetails({ ...formData });
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          preferredDate: '',
          preferredTime: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBookAnother = () => {
    setSubmitStatus('idle');
    setConfirmedDetails(null);
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <section id="booking" className="py-12 sm:py-24 px-4 sm:px-8 bg-white">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 items-start">
          {/* Left - Info */}
          <div className="lg:sticky lg:top-32">
            <div className="inline-flex items-center space-x-2 mb-4">
              <i className="ri-calendar-check-line text-[#0066CC]"></i>
              <span className="text-sm font-semibold text-[#0066CC] uppercase tracking-wider whitespace-nowrap">
                Book an Appointment
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1A2332] mb-4 sm:mb-6 leading-tight">
              Build Your<br />IT Roadmap
            </h2>
            <p className="text-gray-600 leading-relaxed mb-8 sm:mb-10 max-w-md text-sm sm:text-base">
              Schedule a free consultation with our experts. We assess your current infrastructure and recommend tailored solutions to drive your business forward.
            </p>

            <div className="space-y-5 sm:space-y-6">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB] flex items-center justify-center shrink-0">
                  <i className="ri-time-line text-lg sm:text-xl text-[#0066CC]"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-[#1A2332] mb-1 text-sm sm:text-base">30-Minute Session</h4>
                  <p className="text-xs sm:text-sm text-gray-500">Quick yet thorough assessment of your needs</p>
                </div>
              </div>
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB] flex items-center justify-center shrink-0">
                  <i className="ri-shield-star-line text-lg sm:text-xl text-[#0066CC]"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-[#1A2332] mb-1 text-sm sm:text-base">No Obligation</h4>
                  <p className="text-xs sm:text-sm text-gray-500">Free consultation with zero commitment required</p>
                </div>
              </div>
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB] flex items-center justify-center shrink-0">
                  <i className="ri-user-star-line text-lg sm:text-xl text-[#0066CC]"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-[#1A2332] mb-1 text-sm sm:text-base">Expert Guidance</h4>
                  <p className="text-xs sm:text-sm text-gray-500">Speak directly with a senior solutions architect</p>
                </div>
              </div>
            </div>

            <div className="mt-8 sm:mt-10 hidden lg:block">
              <img
                src="https://public.readdy.ai/ai/img_res/f1985548e16d1c595550f0103f2c2afb.jpg"
                alt="IT Consultation"
                className="w-full h-[280px] object-cover object-top rounded-2xl"
              />
            </div>
          </div>

          {/* Right - Form / Confirmation */}
          <div className="bg-[#F8F9FA] rounded-2xl sm:rounded-3xl p-5 sm:p-8 md:p-10 shadow-sm">
            {submitStatus === 'success' && confirmedDetails ? (
              <ConfirmationReceipt details={confirmedDetails} onBookAnother={handleBookAnother} />
            ) : (
              <form
                id="appointment-booking-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div>
                  <h3 className="text-2xl font-bold text-[#1A2332] mb-1">Request an Appointment</h3>
                  <p className="text-sm text-gray-500">Fill in your details and we will get back to you shortly.</p>
                </div>

                {/* Name & Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1A2332] mb-1.5">Full Name *</label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0066CC]/30 focus:border-[#0066CC] transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A2332] mb-1.5">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@company.com"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0066CC]/30 focus:border-[#0066CC] transition-all"
                    />
                  </div>
                </div>

                {/* Phone & Company */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1A2332] mb-1.5">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0066CC]/30 focus:border-[#0066CC] transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A2332] mb-1.5">Company</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0066CC]/30 focus:border-[#0066CC] transition-all"
                    />
                  </div>
                </div>

                {/* Service */}
                <div>
                  <label className="block text-sm font-medium text-[#1A2332] mb-1.5">Service of Interest *</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0066CC]/30 focus:border-[#0066CC] transition-all cursor-pointer appearance-none"
                    style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%236B7280\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                  >
                    <option value="">Select a service</option>
                    {serviceOptions.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1A2332] mb-1.5">Preferred Date *</label>
                    <input
                      type="date"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      required
                      min={today}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0066CC]/30 focus:border-[#0066CC] transition-all cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A2332] mb-1.5">Preferred Time *</label>
                    <select
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0066CC]/30 focus:border-[#0066CC] transition-all cursor-pointer appearance-none"
                      style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%236B7280\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                    >
                      <option value="">Select a time</option>
                      {timeSlots.map(slot => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-[#1A2332] mb-1.5">
                    Additional Details
                    <span className="text-gray-400 font-normal ml-1">({formData.message.length}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) handleChange(e);
                    }}
                    maxLength={500}
                    rows={4}
                    placeholder="Tell us about your project or specific needs..."
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0066CC]/30 focus:border-[#0066CC] transition-all resize-none"
                  />
                </div>

                {/* Error message */}
                {submitStatus === 'error' && (
                  <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-lg text-sm">
                    <i className="ri-error-warning-line text-lg"></i>
                    Something went wrong. Please try again or contact us directly.
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-[#0066CC] text-white rounded-lg font-semibold text-base hover:bg-[#0055AA] transition-all disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="ri-calendar-check-line text-lg"></i>
                      Request Appointment
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  By submitting, you agree to our{' '}
                  <a href="#" className="text-[#0066CC] hover:underline" rel="nofollow">Privacy Policy</a>.
                  We will respond within one business day.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
